import React, { useState, FC, ReactNode } from "react"

import ActionDialog from "@/components/ActionDialog"
import { BlockCode } from "@/hooks/useSubscriptionLimits"
import { Typography } from "@material-ui/core"
import { renderComponent } from "@/utils/node"
import useDidMount from "@/hooks/useDidMount"
import { isSmallScreen } from "@/utils/checkDevice"
import { handleBackButton } from "@/utils/backButton"
import { whatsappLinks } from "@/utils/link"
import ErrorHandlerService from "@/services/ErrorHandler"

import useStyles from "@/components/UpsellDialog/styles"

type AcceptedBlockCodes = Extract<BlockCode,
"user-limit-reached" |
"group-permission-denied" |
"message-blast-permission-denied" |
"integration-permission-denied" |
"chatbot-permission-denied" |
"chatbot-limit-reached" |
"active-campaign-chat-plugin-denied" |
"ai-text-enhancement-limit-reached-attendant" |
"ai-text-enhancement-limit-reached-admin" |
"agent-permission-denied" |
"meeting-analyzer-denied" |
"voice-call-limit-reached" |
"template-send-limit-reached" |
"template-send-limit-reached-attendant" |
"template-send-limit-near-limit" |
"template-send-limit-near-limit-attendant"
>

type DialogueCodeToDialogContentProps = Record< AcceptedBlockCodes, {
	dialogueTitle: string
	dialogueBody: JSX.Element | JSX.Element[]
	link?: string
	dialogButtonText?: string
}>

type UpsellDialogProps = {
	onSave?: () => Promise<void> | void
	onClose?: () => void
	openDialog?: boolean
	dialogCode: BlockCode
}

type UpsellDialogType = {
	open: (props: UpsellDialogProps) => void
}

const UpsellDialogBody: FC<{ children: ReactNode }> = (props) => {
	const { children } = props
	const classes = useStyles()

	return (
		<Typography className={classes.dialogueBodyTypography}>
			{children}
		</Typography>
	)
}

export const dialogCodeToDialogContent: DialogueCodeToDialogContentProps = {
	"user-limit-reached": {
		dialogueTitle: "Parabéns, sua operação parece estar crescendo!",
		dialogueBody: (
			<>
				<Typography style={{ alignItems: "center", whiteSpace: "pre-line" }}>
					Parece que você chegou no limite de usuários do seu plano,
					clique no botão abaixo e contrate usuários adicionais.
				</Typography>
			</>
		),
		link: whatsappLinks.sendMessageToAcquireAditionalUserModule,
		dialogButtonText: "Solicitar usuários adicionais"
	},
	"group-permission-denied": {
		dialogueTitle: "Sua operação precisa do módulo de grupos ativo e funcionando!",
		dialogueBody: (
			<>
				<Typography style={{ alignItems: "center", whiteSpace: "pre-line" }}>
					O módulo de grupos não está disponível no seu plano, mas parece que
					você precisa desse recurso ativo para atingir os resultados do seu negócio?
					Inclua o módulo de grupos no seu plano e aproveite os benefícios!
				</Typography>
			</>
		),
		link: whatsappLinks.sendMessageToAcquireGroupModule,
		dialogButtonText: "Incluir o módulo de grupos"
	},
	"message-blast-permission-denied": {
		dialogueTitle: "Sua operação precisa do módulo de envio em massa ativo e funcionando!",
		dialogueBody: (
			<>
				<Typography style={{ alignItems: "center", whiteSpace: "pre-line" }}>
					O módulo de envio em massa não está disponível no seu plano, mas parece que
					você precisa desse recurso ativo para atingir os resultados do seu negócio?
					Inclua o módulo de envio em massa no seu plano e aproveite os benefícios!
				</Typography>
			</>
		),
		link: whatsappLinks.sendMessageToAcquireMessageBlastModule,
		dialogButtonText: "Incluir o módulo de envio em massa"
	},
	"integration-permission-denied": {
		dialogueTitle: "Sua operação precisa do módulo de integração ativo e funcionando!",
		dialogueBody: (
			<>
				<Typography style={{ alignItems: "center", whiteSpace: "pre-line" }}>
					O módulo de integração não está disponível no seu plano, mas parece que
					você precisa desse recurso ativo para atingir os resultados do seu negócio?
					Inclua o módulo de integração no seu plano e aproveite os benefícios!
				</Typography>
			</>
		),
		link: whatsappLinks.sendMessageToAcquireIntegrationModule,
		dialogButtonText: "Incluir o módulo de integração"
	},
	"chatbot-permission-denied": {
		dialogueTitle: "Sua operação precisa do módulo de Bot ativo e funcionando!",
		dialogueBody: (
			<>
				<Typography style={{ alignItems: "center", whiteSpace: "pre-line" }}>
					O módulo de Bot não está disponível no seu plano, mas parece que
					você precisa desse recurso ativo para atingir os resultados do seu negócio?
					Inclua o módulo de Bot no seu plano e aproveite os benefícios!
				</Typography>
			</>
		),
		link: whatsappLinks.sendMessageToAcquireChatBotModule,
		dialogButtonText: "Incluir o módulo de bot"
	},
	"chatbot-limit-reached": {
		dialogueTitle: "Sua operação certamente precisa desse bot funcionando!",
		dialogueBody: (
			<>
				<Typography style={{ alignItems: "center", whiteSpace: "pre-line" }}>
					Você chegou no limite de bots ativos que seu plano permite e parece que
					precisa de mais, certo? Migre de plano para ter bots
					ilimitados além de vários outros recursos.
				</Typography>
			</>
		),
		link: whatsappLinks.sendMessageToMigrateFromPlan,
		dialogButtonText: "Migrar de plano"
	},
	"active-campaign-chat-plugin-denied": {
		dialogueTitle: "Sua operação precisa do plugin de receber dados do Active funcionando!",
		dialogueBody: (
			<>
				<Typography style={{ alignItems: "center", whiteSpace: "pre-line" }}>
					O módulo de recebimento de dados do Active Campaign não está disponível no seu plano atual.
					Mas, parece que você precisa desse recurso ativo para alcançar melhores resultados para o seu negócio.
					Inclua o módulo no seu plano e aproveite os benefícios!
				</Typography>
			</>
		),
		link: whatsappLinks.sendMessageToAcquireActiveCampaignChatPluginModule,
		dialogButtonText: "Incluir o módulo de receber dados do Active"
	},
	"ai-text-enhancement-limit-reached-attendant": {
		dialogueTitle: "Você atingiu o limite de otimizações com IA! 😥",
		dialogueBody: (
			<>
				<Typography style={{ alignItems: "center", whiteSpace: "pre-line" }}>
					Notamos que as otimizações de texto estão fazendo a diferença no seu dia a dia, melhorando suas interações e elevando suas mensagens! Fale com o nosso time de vendas para liberar ainda mais otimizações para suas mensagens!
				</Typography>
			</>
		),
		link: whatsappLinks.sendMessageToAcquireAdditionalAIEnhancementModule,
		dialogButtonText: "FALAR COM O TIME DE VENDAS"
	},
	"agent-permission-denied": {
		dialogueTitle: "Sua operação precisa do módulo de Agentes de IA ativo e funcionando!",
		dialogueBody: (
			<>
				<Typography style={{ alignItems: "center", whiteSpace: "pre-line" }}>
					O módulo de Agentes de IA não está disponível no seu plano atual, mas parece que
					você precisa desse recurso para automatizar o atendimento do seu negócio?
					Inclua o módulo no seu plano e aproveite os benefícios!
				</Typography>
			</>
		),
		link: whatsappLinks.sendMessageToMigrateFromPlan,
		dialogButtonText: "Falar com um consultor"
	},
	"meeting-analyzer-denied": {
		dialogueTitle: "Sua operação precisa do módulo de análise de reuniões ativo e funcionando!",
		dialogueBody: (
			<>
				<Typography style={{ alignItems: "center", whiteSpace: "pre-line" }}>
					O módulo de análise de reuniões não está disponível no seu plano atual, mas parece que
					você precisa desse recurso para melhorar o desempenho dos seus closers?
					Inclua o módulo no seu plano e aproveite os benefícios!
				</Typography>
			</>
		),
		link: whatsappLinks.sendMessageToMigrateFromPlan,
		dialogButtonText: "Falar com um consultor"
	},
	"ai-text-enhancement-limit-reached-admin": {
		dialogueTitle: "O seu time atingiu o limite de otimizações com IA! 😥",
		dialogueBody: (
			<>
				<Typography style={{ alignItems: "center", whiteSpace: "pre-line" }}>
					Percebemos que as otimizações de texto têm gerado um impacto positivo nas interações da sua equipe, melhorando a qualidade e a eficiência das respostas. Com mais otimizações, você pode potencializar ainda mais o desempenho dos seus atendentes e garantir um atendimento de excelência em cada conversa!
				</Typography>
			</>
		),
		link: whatsappLinks.sendMessageToAcquireAdditionalAIEnhancementModule,
		dialogButtonText: "CONTRATAR AGORA"
	},
	"voice-call-limit-reached": {
		dialogueTitle: "Você chegou no limite de créditos de ligação!",
		dialogueBody: (
			<>
				<Typography style={{ alignItems: "center", whiteSpace: "pre-line" }}>
					Parece que você chegou no limite de créditos de ligação disponíveis no seu plano.{"\n"}
					Faça um upgrade para continuar realizando ligações e não perder nenhum contato importante!
				</Typography>
			</>
		),
		link: whatsappLinks.sendMessageToAcquireVoiceCallMinutesModule,
		dialogButtonText: "Contratar créditos adicionais"
	},
	"template-send-limit-reached": {
		dialogueTitle: "Seus créditos de mensagens estão esgotados!",
		dialogueBody: (
			<>
				<UpsellDialogBody>
					Você atingiu o limite mensal de créditos para envio de mensagens do seu plano.
					Para continuar enviando, contrate créditos adicionais com nosso time comercial.
				</UpsellDialogBody>
			</>
		),
		link: whatsappLinks.sendMessageToAcquireAdditionalWABATemplateCredits,
		dialogButtonText: "Falar com comercial"
	},
	"template-send-limit-reached-attendant": {
		dialogueTitle: "Seus créditos de mensagens estão esgotados!",
		dialogueBody: (
			<>
				<UpsellDialogBody>
					Os créditos mensais de envio de mensagens foram esgotados.
					Entre em contato com o gestor da sua conta para solicitar a contratação de créditos adicionais.
				</UpsellDialogBody>
			</>
		),
		dialogButtonText: "Entendi"
	},
	"template-send-limit-near-limit": {
		dialogueTitle: "Seus créditos de mensagens estão acabando!",
		dialogueBody: (
			<>
				<UpsellDialogBody>
					Os créditos mensais de envio de mensagens estão quase esgotados, para evitar interrupções no envio das suas mensagens, contrate créditos adicionais com nosso time comercial.
				</UpsellDialogBody>
			</>
		),
		link: whatsappLinks.sendMessageToAcquireAdditionalWABATemplateCredits,
		dialogButtonText: "Falar com comercial"
	},
	"template-send-limit-near-limit-attendant": {
		dialogueTitle: "Seus créditos de mensagens estão acabando!",
		dialogueBody: (
			<>
				<UpsellDialogBody>
					Os créditos mensais de envio de mensagens estão quase esgotados.
					Para evitar interrupções no envio das suas mensagens, entre em contato com o gestor da sua conta para solicitar a contratação de créditos adicionais.
				</UpsellDialogBody>
			</>
		),
		dialogButtonText: "Estou ciente"
	}
}

/**
 * dialogCode param should be returned by useSubscriptionLimits hook
 */
const UpsellDialog: UpsellDialogType & React.FC<UpsellDialogProps> = (props) => {
	const {
		dialogCode,
		onClose,
		onSave
	} = props

	const [opened, setOpened] = useState(true)
	const [loading, setLoading] = useState(false)

	const dialogContent = dialogCodeToDialogContent[dialogCode as AcceptedBlockCodes]
	if (!dialogContent) {
		ErrorHandlerService.handle(new Error("UpsellDialog called without content"))
		return <></>
	}

	const dialogueBody = dialogContent.dialogueBody

	const handleClose = async () => {
		await onClose?.()
		setOpened(false)
	}

	const handleSave = async () => {
		setLoading(true)
		await onSave?.()
		if (dialogContent.link) {
			window.open(dialogContent.link, "_blank")
		}
		setLoading(false)
		handleClose()
	}

	useDidMount(() => {
		if (isSmallScreen) {
			handleBackButton(
				() => handleClose()
			)
		}
	})

	return (
		<ActionDialog
			title={dialogContent.dialogueTitle}
			openDialog={opened}
			loading={loading}
			fullWidth={true}
			saveText={dialogContent.dialogButtonText?.toUpperCase() || "RETORNAR"}
			onSave={handleSave}
			onClose={onClose}
		>
			{dialogueBody}
		</ActionDialog>
	)
}

UpsellDialog.open = (props: UpsellDialogProps) => {
	renderComponent(
		props.dialogCode,
		<UpsellDialog
			{...props}
		/>
	)
}

export default UpsellDialog
